import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { SnackbarContent, closeSnackbar } from 'notistack';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
// components
import Iconify from 'src/components/iconify';
// routes
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
// redux
import { sendMessage, reactToMessage, setActiveConversation } from 'src/store/slices/chatSlice';

// ----------------------------------------------------------------------

const ChatNotificationToast = React.forwardRef(({ 
  snackbarId, 
  message, 
  senderName, 
  avatarUrl, 
  conversationName, 
  conversationId 
}, ref) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);

  const handleSendReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    dispatch(sendMessage({ 
      conversationId, 
      body: replyText 
    }));
    
    setReplyText('');
    closeSnackbar(snackbarId);
  };

  const handleReact = (emoji) => {
    dispatch(reactToMessage({ 
      conversationId, 
      messageId: message._id, 
      emoji 
    }));
    closeSnackbar(snackbarId);
  };

  const handleOpenChat = () => {
    dispatch(setActiveConversation(conversationId));
    router.push(`${paths.dashboard.chat}?id=${conversationId}`);
    closeSnackbar(snackbarId);
  };

  const msgSnippet = message.type === 'text' ? message.body : `Sent a ${message.type}`;

  return (
    <SnackbarContent ref={ref} style={{ display: 'block' }}>
      <Box
        sx={{
          width: 320,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: (theme) => theme.customShadows.z20,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.16)}`,
          backgroundImage: (theme) =>
            theme.palette.mode === 'dark'
              ? `linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))`
              : 'none',
        }}
      >
        {/* Header Info */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar 
            src={avatarUrl} 
            alt={senderName} 
            sx={{ width: 40, height: 40, border: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            {senderName?.charAt(0).toUpperCase()}
          </Avatar>

          <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontSize: 13, fontWeight: 'bold' }}>
              {senderName}
              {conversationName && (
                <Box component="span" sx={{ fontWeight: 'normal', color: 'text.secondary', ml: 0.5 }}>
                  @ {conversationName}
                </Box>
              )}
            </Typography>
            <Typography variant="body2" noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
              {msgSnippet}
            </Typography>
          </Stack>

          <IconButton size="small" onClick={() => closeSnackbar(snackbarId)} sx={{ p: 0.5 }}>
            <Iconify icon="mingcute:close-line" width={16} />
          </IconButton>
        </Stack>

        {/* Quick Action Buttons */}
        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 1.5 }}>
          {/* Reaction Emojis */}
          <Stack direction="row" spacing={0.25}>
            {['👍', '❤️', '😂', '🔥'].map((emoji) => (
              <IconButton 
                key={emoji} 
                size="small" 
                onClick={() => handleReact(emoji)}
                sx={{ fontSize: 14, p: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
              >
                {emoji}
              </IconButton>
            ))}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button 
              size="small" 
              variant="soft" 
              color={showReply ? 'primary' : 'inherit'}
              onClick={() => setShowReply(!showReply)}
              startIcon={<Iconify icon="solar:reply-bold" width={14} />}
              sx={{ px: 1, py: 0.5, fontSize: 11, height: 26 }}
            >
              Reply
            </Button>

            <Tooltip title="Open Chat" arrow>
              <IconButton size="small" onClick={handleOpenChat} color="primary" sx={{ p: 0.5 }}>
                <Iconify icon="solar:arrow-right-up-bold" width={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Reply input field */}
        {showReply && (
          <Box component="form" onSubmit={handleSendReply} sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              autoFocus
              placeholder="Type a reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" size="small" color="primary" disabled={!replyText.trim()}>
                    <Iconify icon="solar:send-bold" width={16} />
                  </IconButton>
                ),
                sx: { pr: 0.5, fontSize: 12, height: 32 }
              }}
            />
          </Box>
        )}
      </Box>
    </SnackbarContent>
  );
});

ChatNotificationToast.propTypes = {
  snackbarId: PropTypes.any.isRequired,
  message: PropTypes.object.isRequired,
  senderName: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  conversationName: PropTypes.string,
  conversationId: PropTypes.string.isRequired,
};

export default ChatNotificationToast;
